import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * A link to one record of a list page, e.g. a notification's (notificationTarget): `?record=<ItemId>`, and
 * `?tab=<value>` when the list is on a tab other than the page's first.
 */
export const LINKED_RECORD_PARAM = 'record';
export const LINKED_TAB_PARAM = 'tab';

/** The page's path with a link to one of its records; the bare path without one. */
export const linkToRecord = (path: string, itemId?: string, tab?: string): string => {
  if (!itemId) return path;
  const params = new URLSearchParams({ [LINKED_RECORD_PARAM]: itemId });
  if (tab) params.set(LINKED_TAB_PARAM, tab);
  return `${path}?${params.toString()}`;
};

/**
 * The record the page was linked to (`linkedId`, '' for none) and `clearLinked` to show the whole list
 * again. Hand `linkedId` to `useServerTable`, which then lists only that record.
 */
export const useLinkedRecord = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get(LINKED_RECORD_PARAM) ?? '';
  const linkedTab = searchParams.get(LINKED_TAB_PARAM) ?? '';

  const clearLinked = useCallback(
    () =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(LINKED_RECORD_PARAM);
          next.delete(LINKED_TAB_PARAM);
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  return { linkedId, linkedTab, clearLinked };
};

/** Opens (`open`) the linked record once it is among the listed `items`; once per link. */
export const useOpenLinkedRecord = <TItem extends { ItemId: string }>(
  linkedId: string,
  items: TItem[],
  open: (item: TItem) => void
) => {
  const openedRef = useRef('');
  const item = linkedId ? items.find((row) => row.ItemId === linkedId) : undefined;

  useEffect(() => {
    if (!linkedId) openedRef.current = '';
    if (!item || openedRef.current === linkedId) return;
    openedRef.current = linkedId;
    open(item);
  }, [linkedId, item, open]);
};

/** A page's tab, switched to the linked one (`?tab=`) whenever a link names one. */
export const useLinkedTab = (defaultTab: string) => {
  const { linkedTab } = useLinkedRecord();
  const [tab, setTab] = useState(linkedTab || defaultTab);

  useEffect(() => {
    if (linkedTab) setTab(linkedTab);
  }, [linkedTab]);

  return [tab, setTab] as const;
};
